import { formatISO, parseISO } from 'date-fns';
import { type SAPIDateTime } from '@/types/sapi/scalars';
import {
	type Bill,
	type Contact,
	type Expense,
	type File,
	type Invoice,
	type MarketingList,
	type NewsFeedItem,
	type Order,
	type Project,
	type PurchaseOrder,
	type Quote,
	type RealEstateObject,
	type RentalOrder,
	type User,
	type UnionBill,
	type UnionContact,
	type UnionExpense,
	type UnionFile,
	type UnionInvoice,
	type UnionMarketingList,
	type UnionNewsFeedItem,
	type UnionOrder,
	type UnionProject,
	type UnionPurchaseOrder,
	type UnionQuote,
	type UnionRealEstateObject,
	type UnionRentalOrder,
	type UnionUser,
	type HiddenByPermissions,
	type BackgroundJob,
	type Task,
	type UnionBackgroundJob,
	type UnionTask,
} from '@/generated/graphql';

type Entity =
	| File
	| NewsFeedItem
	| Project
	| RealEstateObject
	| User
	| Contact
	| MarketingList
	| Invoice
	| Order
	| Quote
	| RentalOrder
	| Expense
	| PurchaseOrder
	| Bill
	| Task
	| BackgroundJob;

type UnionEntity =
	| UnionFile
	| UnionNewsFeedItem
	| UnionProject
	| UnionRealEstateObject
	| UnionUser
	| UnionContact
	| UnionMarketingList
	| UnionInvoice
	| UnionOrder
	| UnionQuote
	| UnionRentalOrder
	| UnionExpense
	| UnionPurchaseOrder
	| UnionBill
	| UnionTask
	| UnionBackgroundJob;

/**
 * Convert the given date object to a string following the SAPI DateTime
 * format.
 */
export const stringifySAPIDateTime = (date: Date) => formatISO(date);

/**
 * Parse the given datetime string from SAPI into a Date object.
 *
 * @param sapiDateTime Datetime string in the ISO format
 *	"YYYY-MM-DDTHH:mm:ss+x"
 */
export const parseDateTimeFromSAPI = (sapiDateTime: SAPIDateTime) =>
	parseISO(sapiDateTime);

export const makeUnionEntity = (entity: Entity): UnionEntity => {
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const typeName = entity.__typename!;
	const normalisedTypeName =
		typeName.charAt(0).toLowerCase() + typeName.slice(1);

	return {
		__typename: `Union${typeName}`,
		[`${normalisedTypeName}`]: entity,
	} as UnionEntity;
};

export const isNotHiddenByPermissions = <T extends { __typename?: string }>(
	item: Pick<HiddenByPermissions, '__typename'> | T
): item is T => {
	return item?.__typename !== 'HiddenByPermissions';
};

export const getItemFromHiddenByPermissionUnion = <
	T extends { __typename?: string },
>(
	item: Pick<HiddenByPermissions, '__typename'> | T | null | undefined
): T | null => {
	if (!item) {
		return null;
	}

	return isNotHiddenByPermissions(item) ? item : null;
};

/**
 * @param booleanString "1" or "0"
 */
export const parseBooleanString = (booleanString?: string) => {
	return booleanString === '1';
};

/**
 * @returns "1" or "0"
 */
export const stringifyBoolean = (boolean: boolean) => {
	return boolean ? '1' : '0';
};
